<template>
  <!--
    Globalny kontener powiadomień. Montowany raz, w `CarViver.vue`, i teleportowany
    do <body> — dzięki temu nie łapie `overflow: hidden` ani kontekstów układania
    z paneli aplikacji i zawsze jest nad modalami.

    Toast wsuwa się od prawej krawędzi, tło zielone (sukces) lub czerwone (błąd),
    a pasek na dole wypełnia się od lewej do prawej, pokazując, ile czasu zostało
    do schowania. Najechanie kursorem wstrzymuje odliczanie razem z paskiem.
  -->
  <Teleport to="body">
    <div class="app-notifications" role="region" :aria-label="t('notifications')">
      <TransitionGroup name="app-notification" tag="div" class="app-notifications__stack">
        <div
          v-for="item in notifications.items"
          :key="item.id"
          class="app-notification"
          :class="`app-notification--${item.type}`"
          :role="item.type === 'error' ? 'alert' : 'status'"
          aria-live="polite"
          @mouseenter="notifications.pause(item.id)"
          @mouseleave="notifications.resume(item.id)"
          @focusin="notifications.pause(item.id)"
          @focusout="notifications.resume(item.id)"
        >
          <span class="app-notification__icon" aria-hidden="true"></span>

          <p class="app-notification__message">
            {{ item.message }}
            <span v-if="item.count > 1" class="app-notification__count">×{{ item.count }}</span>
          </p>

          <button
            type="button"
            class="app-notification__close"
            :aria-label="t('close')"
            @click="notifications.dismiss(item.id)"
          >
            ×
          </button>

          <!-- `:key` z runId restartuje animację, gdy ten sam komunikat wpadnie ponownie. -->
          <span
            v-if="item.duration"
            :key="`${item.id}-${item.runId}`"
            class="app-notification__progress"
            :style="{
              animationDuration: `${item.duration}ms`,
              animationPlayState: item.paused ? 'paused' : 'running',
            }"
          ></span>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { useI18n } from "vue-i18n";
import { useNotificationStore } from "@/stores/notification.store";

const { t } = useI18n();
const notifications = useNotificationStore();
</script>

<style>
/* Style celowo NIE są `scoped`: kontener trafia przez Teleport do <body>,
   poza drzewo komponentu, więc atrybuty scope'u i tak by go nie objęły. */

.app-notifications {
  position: fixed;
  top: var(--spacing-lg);
  right: var(--spacing-lg);
  /* Nad wszystkim, co potrafi się otworzyć: modale bootstrapa (1050) oraz menu
     datepickera, które renderuje się z z-index 99999. */
  z-index: 100000;
  width: 380px;
  max-width: calc(100vw - 2 * var(--spacing-lg));
  pointer-events: none; /* klikalne są same toasty, nie pusty obszar kolumny */
}

.app-notifications__stack {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.app-notification {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  overflow: hidden;
  padding: var(--spacing-md) var(--spacing-lg);
  padding-right: var(--spacing-xl);
  border-radius: var(--control-radius);
  box-shadow: var(--shadow-overlay);
  color: var(--text-inverse);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  pointer-events: auto;
}

.app-notification--success {
  background: var(--status-ok);
}

.app-notification--error {
  background: var(--status-error);
}

.app-notification--warning {
  background: var(--status-warn);
}

.app-notification--info {
  background: var(--accent-blue);
}

/* Znacznik typu rysowany CSS-em — bez zależności od zestawu ikon. */
.app-notification__icon {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  position: relative;
}

.app-notification__icon::before {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: var(--font-weight-bold);
  line-height: 1;
}

.app-notification--success .app-notification__icon::before {
  content: "✓";
}

.app-notification--error .app-notification__icon::before {
  content: "!";
}

.app-notification--warning .app-notification__icon::before {
  content: "!";
}

.app-notification--info .app-notification__icon::before {
  content: "i";
}

.app-notification__message {
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
}

.app-notification__count {
  margin-left: var(--spacing-xs);
  opacity: 0.85;
  font-weight: var(--font-weight-bold);
}

.app-notification__close {
  position: absolute;
  top: 4px;
  right: 6px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 18px;
  line-height: 1;
  opacity: 0.75;
  cursor: pointer;
}

.app-notification__close:hover {
  opacity: 1;
}

/* Pasek odliczania: rośnie od lewej do prawej; pełna szerokość = koniec czasu. */
.app-notification__progress {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 3px;
  width: 100%;
  background: rgba(255, 255, 255, 0.75);
  transform: scaleX(0);
  transform-origin: left center;
  animation-name: app-notification-progress;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

@keyframes app-notification-progress {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

/* Wjazd od prawej, wyjazd w prawo. `-move` odpowiada za płynne domykanie luki
   po zamkniętym toaście w stosie. */
.app-notification-enter-active,
.app-notification-leave-active {
  transition: transform 0.28s ease, opacity 0.28s ease;
}

.app-notification-move {
  transition: transform 0.28s ease;
}

.app-notification-enter-from,
.app-notification-leave-to {
  transform: translateX(calc(100% + var(--spacing-lg)));
  opacity: 0;
}

/* Element opuszczający stos jest wyjęty z układu, żeby pozostałe od razu
   przesunęły się w górę zamiast czekać na koniec animacji. */
.app-notification-leave-active {
  position: absolute;
  right: 0;
  width: 100%;
}

@media (prefers-reduced-motion: reduce) {
  .app-notification-enter-active,
  .app-notification-leave-active,
  .app-notification-move {
    transition: none;
  }

  .app-notification__progress {
    animation: none;
    transform: scaleX(1);
  }
}
</style>
